import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, clearSessionToken, setSessionToken } from './api.js';
import type { Org, OrgType, Role, User } from '../../shared/types.js';

export interface SessionData {
  user: User | null;
  org: Org;
  views: { id: string; label: string }[];
  capabilities: string[];
  instructorId: number | null;
  studentId: number | null;
  roleLabel: string;
  roleNote: string;
  /** The demo-account switch: offered to the owner, and the way back out. */
  demo: { canViewAs: boolean; viewingAs: string | null; realName: string | null };
  orgLabel: string;
  orgNote: string;
  gates: boolean;
}

interface SessionApi {
  session: SessionData | null;
  loading: boolean;
  can: (capability: string) => boolean;
  hasView: (view: string) => boolean;
  role: Role | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setOrgType: (type: OrgType) => Promise<void>;
}

const SessionContext = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const data = await api.get<SessionData & { user: User | null }>('/session');
    setSession(data.user ? data : null);
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const value = useMemo<SessionApi>(() => ({
    session,
    loading,
    role: session?.user?.role ?? null,
    can: (capability) => !!session?.capabilities.includes(capability),
    hasView: (view) => !!session?.views.some((v) => v.id === view),
    refresh,
    login: async (email, password) => {
      // The token is only kept by a native shell; a browser ignores it and uses
      // the httpOnly cookie the same response set.
      const { token } = await api.post<{ token?: string }>('/login', { email, password });
      setSessionToken(token);
      await refresh();
    },
    logout: async () => {
      await api.post('/logout');
      clearSessionToken();
      setSession(null);
    },
    setOrgType: async (type) => {
      await api.post('/org-type', { type });
      await refresh();
    },
  }), [session, loading, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider');
  return ctx;
}

/**
 * The role bar is a demo affordance — it exists to show how the app changes by
 * role and organisation type, and would not ship. Hiding it is remembered, so
 * the app can be looked at as a customer would see it.
 */
const ROLEBAR_KEY = 'flightslots.rolebar';

export function useRoleBar(): [boolean, () => void] {
  const [shown, setShown] = useState(() => {
    try { return localStorage.getItem(ROLEBAR_KEY) !== 'hidden'; } catch { return true; }
  });
  const toggle = useCallback(() => {
    setShown((v) => {
      try { localStorage.setItem(ROLEBAR_KEY, v ? 'hidden' : 'shown'); } catch { /* private mode */ }
      return !v;
    });
  }, []);
  return [shown, toggle];
}

/* ── Theme ────────────────────────────────────────────────────────── */

const THEME_KEY = 'flightslots.theme';

export function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState<string>(() => {
    try {
      return localStorage.getItem(THEME_KEY)
        ?? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* private mode */ }
  }, [theme]);

  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}
